import { Injectable, inject } from '@angular/core';
import {
  DEVICE_CREDENTIALS_RECORD_ID,
  PosDeviceCredentials,
  PosOfflineDbService,
} from '@orderna/admin-panel/src/app/services/shared/offline/pos-offline-db.service';
import { CashRegisterDeviceApiService } from '../../cash-drawers/cash-register-device/cash-register-device-api.service';
import { DeviceStatus } from './types/device-status';
import { CashRegisterDeviceChallengePurpose as ChallengePurpose } from '../../cash-drawers/cash-register-device/enums/cash-register-device-challenge-purpose.enum';
import { DeviceAuthenticatedRequestDto } from '../../cash-drawers/cash-register-device/dtos/partials/device-authenticated.request.dto';

@Injectable({
  providedIn: 'root',
})
export class CurrentDeviceCredentialsService {
  private static readonly KEY_ALGORITHM: EcKeyGenParams = {
    name: 'ECDSA',
    namedCurve: 'P-256',
  };

  private static readonly SIGN_ALGORITHM: EcdsaParams = {
    name: 'ECDSA',
    hash: 'SHA-256',
  };

  #db = inject(PosOfflineDbService);
  #api = inject(CashRegisterDeviceApiService);

  async verify(): Promise<DeviceStatus> {
    const registerId = await this.getRegisteredRegisterId();

    if (!registerId) {
      return 'credentials_missing';
    }

    const signedChallenge = await this.getSignedChallenge(
      ChallengePurpose.UI_VERIFICATION,
    );

    const result = await this.#api.verify(registerId, signedChallenge);

    return result.status;
  }

  async getSignedChallenge(
    purpose: ChallengePurpose,
  ): Promise<DeviceAuthenticatedRequestDto> {
    const registerId = await this.getRegisteredRegisterId();

    if (!registerId) {
      throw new Error('Device credentials are missing.');
    }

    const challenge = await this.#api.getChallenge(registerId, {
      purpose,
    });

    const signature = await this.signChallenge(challenge.challenge);

    return {
      challengeId: challenge.challengeId,
      signature,
    };
  }

  async getRegisteredRegisterId(): Promise<number | null> {
    this.ensureSupported();

    const credentials = await this.getCredentials();

    return this.isValidCredentials(credentials)
      ? credentials.cashRegisterId
      : null;
  }

  async generateKeyPair(registerId: number): Promise<void> {
    const keyPair = await crypto.subtle.generateKey(
      CurrentDeviceCredentialsService.KEY_ALGORITHM,
      false, // IMPORTANT: Keep non-extractable. The private key must never be exportable.
      ['sign', 'verify'],
    );

    await this.saveCredentials(
      registerId,
      keyPair.privateKey,
      keyPair.publicKey,
    );
  }

  async exportPublicKey(): Promise<string> {
    const keys = await this.getRequiredCredentials();

    const spki = await crypto.subtle.exportKey('spki', keys.publicKey);

    return this.arrayBufferToBase64Url(spki);
  }

  async signChallenge(challenge: string): Promise<string> {
    const keys = await this.getRequiredCredentials();

    const challengeBuffer = this.base64UrlToArrayBuffer(challenge);

    const signatureBuffer = await crypto.subtle.sign(
      CurrentDeviceCredentialsService.SIGN_ALGORITHM,
      keys.privateKey,
      challengeBuffer,
    );

    return this.arrayBufferToBase64Url(signatureBuffer);
  }

  async deleteKeys(): Promise<void> {
    await this.#db.deviceCredentials.delete(DEVICE_CREDENTIALS_RECORD_ID);
  }

  private base64UrlToArrayBuffer(base64Url: string): ArrayBuffer {
    // Convert Base64URL to standard Base64
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Restore padding
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    // Decode Base64
    const binary = atob(base64);

    // Convert binary string to ArrayBuffer
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
  }

  private arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);

    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }

    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async getRequiredCredentials(): Promise<PosDeviceCredentials> {
    const credentials = await this.getCredentials();

    if (!this.isValidCredentials(credentials)) {
      throw new Error('Device credentials are incomplete.');
    }

    return credentials;
  }

  private async getCredentials(): Promise<PosDeviceCredentials | undefined> {
    return this.#db.deviceCredentials.get(DEVICE_CREDENTIALS_RECORD_ID);
  }

  private async saveCredentials(
    cashRegisterId: number,
    privateKey: CryptoKey,
    publicKey: CryptoKey,
  ): Promise<void> {
    await this.#db.deviceCredentials.put({
      id: DEVICE_CREDENTIALS_RECORD_ID,
      cashRegisterId,
      privateKey,
      publicKey,
    });
  }

  private ensureSupported() {
    if (!globalThis.crypto?.subtle) {
      throw new Error('Web Crypto API is not supported.');
    }
  }

  private isValidCredentials(
    credentials: PosDeviceCredentials | undefined,
  ): credentials is PosDeviceCredentials {
    return (
      credentials != null &&
      credentials.privateKey != null &&
      credentials.publicKey != null &&
      credentials.cashRegisterId != null
    );
  }
}
